/* eslint-disable react-native/no-inline-styles */
import React, { useContext, useState, useEffect, useCallback, useRef } from 'react'
import { View, StyleSheet, FlatList, ScrollView, TouchableOpacity } from 'react-native'
import type { ListRenderItem } from 'react-native'
import { Text, Radio, Button, RangeDatepicker, Card, Icon, Spinner, Modal, Layout, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import ClientSearchBox from './ClientSearchBox'
import { useTranslation } from 'react-i18next'
import CreateMarketingListScreen from '../ClientSegmentation/CreateMarketingListScreen'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns'
import { BonusValueChange } from '../../components/BonusValueChange'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate } from '../../utils/timezoneFunctions'
import { localeDateServiceAtom, userRoleAtom, constantsAtom, storesDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { fetchClientsFromSupabase } from '../../utils/supabaseDataHelpers'
import { shouldUseSupabase } from '../../global/Firebase/types'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { CreateClientView } from '../../components/CreateClientView'
import HeaderIconButton from '../../components/HeaderIconButton'
import OperationsList from '../../components/OperationsList'
import StatsCards from '../../components/StatsCards'
import * as Sentry from '@sentry/react-native'
import { clientsApi } from '../../global/API/typedApiClient'
import { searchClientsWithTelemetry, type SearchedClient } from '../../utils/clientSearch'

// Type imports
import type { ClientResponse } from '@atlas/api-client'

// Types for untyped components and external data
interface UserContextType {
  user: {
    store: { id: string }
    organizationId: string
    role: string
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any
}

interface Constants {
  loyaltySettings?: {
    defaultBonusLevel?: number
  }
}

// Types
type QueryType = 'registrationDate' | 'phoneAndName' | 'product' | 'lastPurchaseDate'
type DateRangePreset = 'last7days' | 'last30days' | 'thisWeek' | 'thisMonth'

interface DateRange {
  startDate: Date
  endDate: Date
}

interface ClientStats {
  total: number
  new: number
  active: number
}

// Extend ClientResponse with additional fields for display
// Note: ref, phoneNumber, registrationDate are already in ClientResponse, don't override
// ATL-1445 stage 3 P6: a search result may also be a client this device created offline whose write
// is not acknowledged yet (`PendingClient`: no Supabase id) — nothing here reads `supabaseId`.
type ClientWithSource = SearchedClient & {
  querySource: 'supabase' | 'firebase'
  displayName?: string
  bonusAmount?: number
  products?: Record<string, { name: string; totalChecks: number; totalQuantity: number }>
}

interface NavigationProp {
  setOptions: (options: { headerRight?: () => React.ReactNode }) => void
  navigate: (screen: string, params?: Record<string, unknown>) => void
}

interface ClientsScreenProps {
  navigation: NavigationProp
}

// Simple debounce function
function debounce<T extends (...args: unknown[]) => void>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      if (timeout) clearTimeout(timeout)
      func(...args)
    }
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  } as T
}

export default function ClientsScreen({ navigation }: ClientsScreenProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context] = useContext(UserContext) as [UserContextType, unknown]
  const [queryType, setQueryType] = useState<QueryType>('registrationDate')
  const userRole = useAtomValue(userRoleAtom) as { permissions?: Record<string, boolean> } | null
  const constants = useAtomValue(constantsAtom) as Constants | null
  const storesDict = useAtomValue(storesDictAtom) as Record<string, unknown>
  // Check device-level setting for backend choice
  const useSupabase = shouldUseSupabase()
  const [store, setStore] = useState<string>(context.user.store.id)
  const [range, setRange] = useState<DateRange>({ startDate: new Date(), endDate: new Date() })
  const [data, setData] = useState<ClientWithSource[] | null>(null)
  const [showBonusChangeModal, setShowBonusChangeModal] = useState(false)
  const [createClientModalVisible, setCreateClientModalVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<ClientWithSource[]>([])
  const [isSearching, setIsSearching] = useState(false)
  // Kept separate from `searchResults.length === 0` so a failed request is never
  // rendered as "nothing found" (ATL-742).
  const [searchFailed, setSearchFailed] = useState(false)
  const [clientStats, setClientStats] = useState<ClientStats>({ total: 0, new: 0, active: 0 })
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangePreset>('last7days')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _rangePicker = useRef<any>(null)
  const localeDateService = useAtomValue(localeDateServiceAtom)

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          iconName='plus-outline'
          testID='clients-add-button'
          onPress={() => {
            setCreateClientModalVisible(true)
          }}
        />
      )
    })
  }, [navigation])

  const navigateToDetail = (client: ClientWithSource) => {
    if (userRole?.permissions?.['ClientDetail'] || context.user.role === 'admin') {
      navigation.navigate('ClientDetail', { client })
    }
  }

  const handleClientSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([])
      setSearchFailed(false)
      return
    }

    setIsSearching(true)
    try {
      const result = await searchClientsWithTelemetry(query, undefined, {
        screen: 'ClientsScreen',
        organizationId: context.user.organizationId,
        storeId: store,
      })
      if (result.status === 'error') {
        // Distinct from "not found": an empty list here previously hid network
        // failures, 401s and response-validation rejections (ATL-742).
        setSearchFailed(true)
        setSearchResults([])
        return
      }
      setSearchFailed(false)
      const normalizedResults: ClientWithSource[] = result.clients.map((client) => ({
        ...client,
        querySource: 'supabase' as const,
      }))
      setSearchResults(normalizedResults)
    } catch (error) {
      // searchClientsWithTelemetry never throws, so this only catches an
      // unexpected local failure. It must stay: callers invoke this as
      // `void handleClientSearch(...)`, so a rejection here would surface as an
      // unhandled rejection (the ATL-562 flood class) instead of a UI state.
      Sentry.captureException(error, {
        tags: { client_search_error: true, screen: 'ClientsScreen' },
      })
      setSearchFailed(true)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(((query: string) => {
      void handleClientSearch(query)
    }) as (...args: unknown[]) => void, 300),
    [context.user.organizationId]
  )

  const findClientsByLastPurchaseDate = async () => {
    setIsLoading(true)
    const localStoreRange = dateRangeWithTimezoneAndAutoclose(range, [store])

    try {
      if (useSupabase) {
        // Use Supabase helper for filtered client queries
        const clients = await fetchClientsFromSupabase(
          context.user.organizationId,
          {
            storeId: store,
            lastPurchaseDateFrom: localStoreRange.startDate,
            lastPurchaseDateTo: localStoreRange.endDate,
          }
        ) as ClientResponse[]
        const clientsWithSource: ClientWithSource[] = clients.map(client => ({
          ...client,
          querySource: 'supabase' as const
        }))
        setData(clientsWithSource)
      } else {
        // Use Firestore (original)
        const querySnapshot = await context.db.collection('clients').where('organizationId', '==', context.user.organizationId).where('lastPurchaseSource', '==', store).where('lastPurchaseDate', '>=', localStoreRange.startDate).where('lastPurchaseDate', '<=', localStoreRange.endDate).get()
        const clients: ClientWithSource[] = []
        querySnapshot.forEach(function(doc: { data: () => Record<string, unknown>; id: string; ref: Record<string, unknown> }) {
          clients.push({ ...doc.data(), id: doc.id, ref: doc.ref, querySource: 'firebase' } as ClientWithSource)
        })
        setData(clients)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
      // Don't fallback to Firestore - throw error instead
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Get date range based on selected preset
  const getDateRangeFromPreset = (preset: DateRangePreset): DateRange => {
    const now = new Date()
    switch (preset) {
      case 'last7days':
        return {
          startDate: subDays(now, 7),
          endDate: now
        }
      case 'last30days':
        return {
          startDate: subDays(now, 30),
          endDate: now
        }
      case 'thisWeek':
        return {
          startDate: startOfWeek(now),
          endDate: endOfWeek(now)
        }
      case 'thisMonth':
        return {
          startDate: startOfMonth(now),
          endDate: endOfMonth(now)
        }
      default:
        return {
          startDate: subDays(now, 7),
          endDate: now
        }
    }
  }

  // Fetch client statistics using Supabase API (only available in Supabase mode)
  const fetchClientStats = async (dateRangePreset: DateRangePreset) => {
    // Client stats API only available in Supabase mode
    if (!useSupabase) {
      setClientStats({ total: 0, new: 0, active: 0 })
      return
    }

    setIsStatsLoading(true)
    try {
      const dateRange = getDateRangeFromPreset(dateRangePreset)
      const localStoreRange = dateRangeWithTimezoneAndAutoclose(dateRange, [store])

      const stats = await clientsApi.getClientStats({
        registrationDateFrom: localStoreRange.startDate?.toISOString(),
        registrationDateTo: localStoreRange.endDate?.toISOString(),
        lastPurchaseDateFrom: localStoreRange.startDate?.toISOString(),
        lastPurchaseDateTo: localStoreRange.endDate?.toISOString(),
      })

      setClientStats({
        total: stats.total,
        new: stats.new,
        active: stats.active
      })
    } catch (error) {
      console.error('Error fetching client stats:', error)
      setClientStats({ total: 0, new: 0, active: 0 })
    } finally {
      setIsStatsLoading(false)
    }
  }

  // Load stats when component mounts or date range changes
  useEffect(() => {
    void fetchClientStats(selectedDateRange)
  }, [selectedDateRange, store])



  const dateText = (
    date: string | Date | { seconds?: number; _seconds?: number } | undefined | null
  ): string | undefined => {
    let correctDate: Date
    if (date) {
      if (typeof date === 'string') {
        // API returns ISO strings
        correctDate = new Date(date)
      } else if (typeof date === 'object' && '_seconds' in date && date._seconds) {
        correctDate = new Date(date._seconds * 1000)
      } else if (typeof date === 'object' && 'seconds' in date && date.seconds) {
        correctDate = new Date(date.seconds * 1000)
      } else if (date instanceof Date) {
        correctDate = date
      } else {
        return undefined
      }
      return format(getLocalStoreDate(store, correctDate), 'dd MMMM yyyy')
    }
    return undefined
  }

  const renderItemFieldsToShow = (item: ClientWithSource, _date: unknown) => {
    const registrationDate = dateText(item?.registrationDate)

    return (
      <>
        <View style={styles.rowItem}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{item?.displayName || item?.name || t('clients.unnamed')}</Text>
        </View>

        <View style={styles.rowItem}>
          <Icon name='phone-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{item?.phoneNumber || t('clients.noPhone')}</Text>
        </View>

        <View style={styles.rowItem}>
          <Icon name='person-add-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{registrationDate || t('clients.noRegistrationDate')}</Text>
        </View>

        {item?.bonusAmount !== undefined && (
          <View style={styles.rowItem}>
            <Icon name='award-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
            <Text category='s2'>{t('clients.bonusAmount', { bonusAmount: item.bonusAmount })}</Text>
          </View>
        )}
      </>
    )
  }

  const renderItem: ListRenderItem<ClientWithSource> = ({ item }) => {
    const registrationDate = dateText(item?.registrationDate)
    return (
      <Card
        style={styles.item}
        onPress={() => navigateToDetail(item)}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{item?.displayName || item?.name}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Icon name='phone-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text category='s2'>{item?.phoneNumber ?? ''}</Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='person-add-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.hitTitle}
            category='s2'>
            {registrationDate}
          </Text>
        </View>
        { item.products && Object.entries(item.products).map(([key, product]) => (
          <View key={key} style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{ flex: 5 }}>{product.name}</Text>
            <Text style={{ flex: 1 }}>{product.totalChecks}</Text>
            <Text style={{ flex: 1 }}>{product.totalQuantity}</Text>
          </View>
        ))}
      </Card>
    )
  }


  return (
    <ScrollView style={styles.container}>
      <StatsCards
        stats={[
          {
            icon: 'people-outline',
            color: '#007AFF',
            value: clientStats.total,
            label: t('clients.totalClients')
          },
          {
            icon: 'person-add-outline',
            color: '#34C759',
            value: clientStats.new,
            label: t('clients.newClients')
          },
          {
            icon: 'activity-outline',
            color: '#FF9500',
            value: clientStats.active,
            label: t('clients.activeClients')
          }
        ]}
        isLoading={isStatsLoading}
        selectedDateRange={selectedDateRange}
        onDateRangeChange={setSelectedDateRange}
        title={t('clients.clientStats')}
        dateRangePresets={[
          { key: 'last7days', label: t('clients.last7Days') },
          { key: 'last30days', label: t('clients.last30Days') },
          { key: 'thisWeek', label: t('clients.thisWeek') },
          { key: 'thisMonth', label: t('clients.thisMonth') }
        ]}
        layout={undefined}
      />

      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('clients.searchClients')}
        </Text>
        <Radio style={styles.radio} checked={queryType === 'registrationDate'} onChange={() => { setQueryType('registrationDate') }}>
          {t('clients.byRegistrationDate')}
        </Radio>
        <Radio style={styles.radio} checked={queryType === 'phoneAndName'} onChange={() => { setQueryType('phoneAndName') }}>
          {t('clients.byPhoneAndName')}
        </Radio>
        <Radio style={styles.radio} checked={queryType === 'product'} onChange={() => { setQueryType('product') }}>
          {t('clients.byProducts')}
        </Radio>
        <Radio style={styles.radio} checked={queryType === 'lastPurchaseDate'} onChange={() => { setQueryType('lastPurchaseDate') }}>
          {t('clients.byLastPurchaseDate')}
        </Radio>
      </View>
      { queryType === 'registrationDate' && (
        <View style={styles.operationsListContainer}>
          <OperationsList
            collectionName='clients'
            dateField='registrationDate'
            additionalFilters={undefined}
            detailScreenName='ClientDetail'
            topView={() => null}
            initialRange='allTime'
            showTotalSum={false}
            showStoreFilter={false}
            showAddButton={false}
            additionalDataValidation={() => {}}
            detailScreenParams={{}}
            renderItemFieldsToShow={(item, date) => renderItemFieldsToShow(item as unknown as ClientWithSource, date)}
            navigation={navigation}
          />
        </View>
      )}
      { queryType === 'phoneAndName' && (
        <View style={styles.paddedContainer}>
          <Text category='label' style={{ marginBottom: 5 }}>
            {t('clients.phoneAndName')}
          </Text>
          <ClientSearchBox
            organizationId={context.user.organizationId}
            style={{ marginBottom: 20 }}
            onSearch={debouncedSearch}
          />
          {isSearching ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <FlatList
              data={searchResults}
              keyExtractor={(item) => item.id}
              ItemSeparatorComponent={() => <View style={[styles.separator, {borderColor: theme['border-basic-color-3']}]} />}
              renderItem={renderItem}
              ListEmptyComponent={() => searchResults.length === 0 ? (
                <Text appearance='hint' style={{
                  marginTop: 100,
                  textAlign: 'center'
                }}>
                  {searchFailed ? t('clientSearchFailed') : t('notFound')}
                </Text>
              ) : null}
            />
          )}
        </View>
      )}
      { queryType === 'product' && (
        <View style={styles.paddedContainer}>
          <CreateMarketingListScreen navigation={navigation} renderItem={renderItem} />
        </View>
      )}
      { queryType === 'lastPurchaseDate' && (
        <View style={styles.paddedContainer}>
          <StoreChooserHorizontal store={store} style={{}} onChange={(newStore: string) => {
            setStore(newStore)
          }} />
          <Text category='label' style={styles.label}>
            {t('clients.' + queryType)}
          </Text>
          <RangeDatepicker
            placement='top'
            dateService={localeDateService}
            range={range}
            min={new Date('2020-12-01')}
            max={new Date('2029-12-31')}
            ref={_rangePicker}
            onSelect={(newRange: { startDate?: Date; endDate?: Date }) => {
              if (newRange.endDate) {
                setRange(newRange as DateRange)
                _rangePicker.current?.blur()
              } else if (newRange.startDate && range.startDate === newRange.startDate) {
                const endDate = new Date(newRange.startDate)
                endDate.setHours(23, 59, 59, 99)
                setRange({ startDate: newRange.startDate, endDate })
                _rangePicker.current?.blur()
              } else {
                setRange(newRange as DateRange)
              }
            }}
          />
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <Button style={{ marginVertical: 10 }} onPress={findClientsByLastPurchaseDate}>
              {t('find')}
            </Button>
          )}

          { data && data.length > 0 && (
            <View>
              <Button style={{ marginVertical: 10 }} onPress={() => setShowBonusChangeModal(true)}>
                {t('clients.addBonuses')}
              </Button>
            </View>
          )}
          { data && (
            <FlatList
              data={data}
              renderItem={renderItem}
              ListEmptyComponent={() => (<Text appearance='hint' style={{
                marginTop: 100,
                textAlign: 'center'
              }}>
                  {t('notFound')}
                </Text>
              )}
              ListHeaderComponent={() => (<Text style={{ marginBottom: 10 }}>{t('clients.count', { count: data.length })}</Text>)}
            />
          )}

          <Modal backdropStyle={styles.backdrop} visible={showBonusChangeModal}
            onBackdropPress={() => setShowBonusChangeModal(false)} style={[styles.modal, {backgroundColor: theme['background-basic-color-1']}]}
          >
            <Layout style={styles.modalContainer}>
              <Text style={{ marginBottom: 10, marginTop: 10 }} category="label">
                {t('clients.addBonuses')}
              </Text>
              <BonusValueChange db={context.db} client={data}
                onBonusUpdate={(_newBonusValue: number) => {
                  setShowBonusChangeModal(false)
                  void findClientsByLastPurchaseDate()
                }}
              />
              <Button status='basic' onPress={() => setShowBonusChangeModal(false)}
                style={{ marginTop: 10 }}
              >
                {t('cancel')}
              </Button>
            </Layout>
          </Modal>
        </View>
      )}

      <Modal style={[styles.modal, { padding: 30, backgroundColor: theme['background-basic-color-1'] }]}
        visible={createClientModalVisible}
        backdropStyle={styles.backdrop}
        onBackdropPress={() => setCreateClientModalVisible(false)}
      >
        <TouchableOpacity
          style={{ position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center' }}
          onPress={() => {
            setCreateClientModalVisible(false)
          }}
        >
          <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
        </TouchableOpacity>
{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
{React.createElement(CreateClientView as React.ComponentType<any>, {
          db: context.db,
          defaultBonusLevel: constants?.loyaltySettings?.defaultBonusLevel,
          store: store,
          storeInfo: storesDict?.[store],
          organizationId: context.user.organizationId,
          onCreate: (client: ClientWithSource) => {
            setCreateClientModalVisible(false)
            // Optionally refresh the search results or navigate to the new client
            if (queryType === 'phoneAndName') {
              // Trigger a search to show the new client
              void handleClientSearch(client.phoneNumber || '')
            }
          }
        })}
      </Modal>

      <View style={{ height: 100 }}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  paddedContainer: {
    paddingHorizontal: 20,
  },
  label: {
    marginTop: 5,
    marginBottom: 5,
  },
  radio: {
    marginBottom: 5
  },
  dateFilter: {
    marginHorizontal: 20,
    marginTop: 10,
    marginBottom: 10
  },
  item: {

  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  hitTitle: {
    // Add any specific styles if needed
  },
  icon: {
    marginRight: 4,
  },
  spinner: {
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 10
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    borderRadius: 20,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 320,
    padding: 32,
    height: '80%',
    borderRadius: 20,
  },
  separator: {
    borderBottomWidth: 1,

  },
  operationsListContainer: {
    flex: 1,
  },
  rowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4
  },
})
